/**
 * pushStore  – Step 3
 *
 * Tracks web push subscription state in memory.
 *
 * ── Why in-memory only ───────────────────────────────────────────────────────
 * The subscription endpoint is stored in Supabase (source of truth).
 * localStorage tracks only whether we've already prompted this browser
 * so we don't re-ask on every load.
 *
 * ── State machine ────────────────────────────────────────────────────────────
 *
 *  'idle'          – not yet checked
 *  'unsupported'   – Push API not available in this browser
 *  'denied'        – user explicitly blocked notifications
 *  'subscribed'    – subscription active and stored in Supabase
 *  'not_subscribed'– supported, not denied, but no active subscription
 */

import { create } from 'zustand';

const PROMPTED_KEY = 'fs_push_prompted';

export type PushStatus =
  | 'idle'
  | 'unsupported'
  | 'denied'
  | 'subscribed'
  | 'not_subscribed';

interface PushState {
  status: PushStatus;

  /** True if we already asked for permission in this browser. */
  hasPrompted: boolean;

  /** The active PushSubscription object (null until subscribed). */
  subscription: PushSubscription | null;

  setStatus:      (status: PushStatus) => void;
  setSubscription: (sub: PushSubscription | null) => void;
  markPrompted:   () => void;
}

export const usePushStore = create<PushState>()((set) => ({
  status:      'idle',
  hasPrompted: (() => {
    try { return localStorage.getItem(PROMPTED_KEY) === '1'; } catch { return false; }
  })(),
  subscription: null,

  setStatus:      (status) => set({ status }),
  setSubscription: (subscription) => set({ subscription }),
  markPrompted: () => {
    try { localStorage.setItem(PROMPTED_KEY, '1'); } catch { /* quota */ }
    set({ hasPrompted: true });
  },
}));
